/**
 * extractDisplayPayload — U3 (QA feedback): unwraps a `RequestEntry.response`
 * (the parsed `handler_result` envelope, `V4HandlerResultSchema` per
 * src/shared/schemas/v4-execution.ts: `{ ok, code, message, meta? }`) down to
 * the actual reply data for the Requests detail view's "Pretty" mode. Raw
 * mode is untouched by this — it always renders `entry.response` directly,
 * full envelope included; only Pretty routes through here (RequestDetail.tsx).
 *
 * Precedence, matching how the write side actually populates `meta`:
 *  1. `meta.result` — the Clay per-item result object for a `clay_routine`
 *     handler (execution-handlers/clay-routine.ts sets
 *     `meta: { summary, routine_run_id, result: item.result }`).
 *  2. `meta` itself — a handler with no `result` sub-field (e.g. a webhook
 *     failure's `meta: { httpStatus }`) still has real per-run detail worth
 *     showing, just not nested under `result`.
 *  3. The whole payload, unchanged — no `meta` at all. Covers both a
 *     non-`clay_routine` handler whose envelope carries no `meta`, and any
 *     unexpected/degraded shape (a bare object, a primitive, `null`) that
 *     doesn't look like a handler-result envelope to begin with. Never
 *     throws on an unexpected shape (Zod-safe-degrade rule) — worst case
 *     Pretty mode falls back to showing exactly what Raw mode would.
 */
export function extractDisplayPayload(response: unknown): unknown {
  if (response === null || typeof response !== 'object') return response;
  const envelope = response as Record<string, unknown>;
  const meta = envelope.meta;
  if (meta === null || typeof meta !== 'object') return response;
  const metaObj = meta as Record<string, unknown>;
  return 'result' in metaObj ? metaObj.result : metaObj;
}
